import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Layers, LayoutGrid, Camera, X, Loader2, Sparkles, SlidersHorizontal } from "lucide-react";
import useDeptCounts from "@/hooks/useDeptCounts";
import { motion, AnimatePresence } from "framer-motion";
import http from "@/api";
import { useApp } from "@/context";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { ProductGridSkeleton } from "@/components/Skeleton";
import CategoryCardGrid from "@/components/catalog/CategoryCardGrid";

const BUDGETS = [
  { ar: "أقل من 100", en: "Under 100", min: null, max: 100 },
  { ar: "100 - 200", en: "100 - 200", min: 100, max: 200 },
  { ar: "200 - 300", en: "200 - 300", min: 200, max: 300 },
  { ar: "300 - 400", en: "300 - 400", min: 300, max: 400 },
  { ar: "400 - 600", en: "400 - 600", min: 400, max: 600 },
  { ar: "أكثر من 600", en: "600+", min: 600, max: null },
];
const COLORS = [
  { ar: "أحمر", en: "Red", val: "أحمر", dot: "#dc2626" },
  { ar: "أبيض", en: "White", val: "أبيض", dot: "#ffffff" },
  { ar: "وردي", en: "Pink", val: "وردي", dot: "#ec4899" },
  { ar: "أصفر", en: "Yellow", val: "أصفر", dot: "#eab308" },
  { ar: "أسود", en: "Black", val: "أسود", dot: "#111111" },
  { ar: "برتقالي", en: "Orange", val: "برتقالي", dot: "#f97316" },
  { ar: "بنفسجي", en: "Purple", val: "بنفسجي", dot: "#8b5cf6" },
  { ar: "أزرق", en: "Blue", val: "أزرق", dot: "#3b82f6" },
];
const OCCASIONS = [
  { ar: "عيد الأم", en: "Mother's Day", val: "عيد الأم" },
  { ar: "عيد ميلاد", en: "Birthday", val: "عيد ميلاد" },
  { ar: "تخرّج", en: "Graduation", val: "تخرج" },
  { ar: "مولود جديد", en: "Newborn", val: "مولود" },
  { ar: "حب ورومانسية", en: "Love", val: "حب" },
  { ar: "شكراً", en: "Thank you", val: "شكر" },
  { ar: "زواج", en: "Wedding", val: "زواج" },
  { ar: "تهنئة", en: "Congrats", val: "تهنئة" },
];
const SORTS = [
  { key: "featured", ar: "المميّز", en: "Featured" },
  { key: "bestselling", ar: "الأكثر مبيعاً", en: "Bestselling" },
  { key: "rating", ar: "الأعلى تقييماً", en: "Top rated" },
  { key: "new", ar: "الأحدث", en: "Newest" },
  { key: "price_asc", ar: "الأرخص", en: "Price ↑" },
  { key: "price_desc", ar: "الأغلى", en: "Price ↓" },
];

export default function Shop() {
  const { t, lang, categories: allCategories } = useApp();
  const [sp, setSp] = useSearchParams();
  const deptSlug = sp.get("department") || "";
  const category = sp.get("category") || "";
  const categoryId = sp.get("category_id") || "";
  const view = sp.get("view") || "list";  // "list" (default chips+products) or "cards" (image grid of cats)
  // تصنيفات القسم المحدد — تظهر كصف فرعي عند اختيار قسم (طلب يونيو 2026)
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  const imgOf = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);   // backend search results (null = not searching)
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imgSearching, setImgSearching] = useState(false);
  const [imgInfo, setImgInfo] = useState(null);    // {description, keywords, count} from image search
  const fileRef = React.useRef(null);

  // Quick filters
  const [showFilters, setShowFilters] = useState(false);
  const [budgetIdx, setBudgetIdx] = useState(null);
  const [color, setColor] = useState("");
  const [occasion, setOccasion] = useState("");
  const [sortKey, setSortKey] = useState("featured");

  const buildFilterParams = React.useCallback(() => {
    const p = {};
    if (sortKey && sortKey !== "featured") p.sort = sortKey;
    if (budgetIdx !== null) {
      const b = BUDGETS[budgetIdx];
      if (b.min !== null) p.price_min = b.min;
      if (b.max !== null) p.price_max = b.max;
    }
    if (color) p.color = color;
    if (occasion) p.occasion = occasion;
    return p;
  }, [sortKey, budgetIdx, color, occasion]);

  const activeFilterCount = (budgetIdx !== null ? 1 : 0) + (color ? 1 : 0) + (occasion ? 1 : 0) + (sortKey !== "featured" ? 1 : 0);
  const clearFilters = () => { setBudgetIdx(null); setColor(""); setOccasion(""); setSortKey("featured"); };

  const onImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImgSearching(true); setImgInfo(null); setResults(null); setQ("");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("lang", lang === "ar" ? "ar" : "en");
      const { data } = await http.post("/ai/image-search", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data.products || []);
      setImgInfo({
        description: lang === "ar" ? data.description_ar : (data.description_en || data.description_ar),
        keywords: data.keywords || [],
        count: data.count || (data.products || []).length,
        matchQuality: data.match_quality || "similar",
        exactCount: data.exact_count || 0,
        visualCount: data.visual_count || 0,
      });
    } catch (err) {
      setResults([]);
      setImgInfo({ description: "", keywords: [], count: 0, error: true });
    } finally { setImgSearching(false); }
  };

  const clearImageSearch = () => { setImgInfo(null); setResults(null); };

  useEffect(() => {
    const _b = localStorage.getItem("rg_branch_id") || "";
    http.get(`/departments?country=${localStorage.getItem("rg_country") || "QA"}${_b ? `&branch_id=${_b}` : ""}`)
      .then(({ data }) => setDepartments(data || []))
      .catch(() => setDepartments([]));
  }, []);

  /* ترقيم صفحات — يعرض كل منتجات القسم مهما كان عددها (60 منتجاً لكل دفعة) */
  const PAGE_SIZE = 60;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (offset) => {
    const params = { ...buildFilterParams(), limit: PAGE_SIZE, offset };
    if (category) params.category = category;
    if (categoryId) params.category_id = categoryId;
    if (deptSlug) params.department = deptSlug;
    // التصنيف يحدد نطاقه بنفسه — إرسال القسم معه يخفي المنتجات المرتبطة بتصنيف من قسم آخر
    if (deptSlug && !category && !categoryId) params.department = deptSlug;
    const { data } = await http.get("/products", { params });
    return data || [];
  }, [category, categoryId, deptSlug, buildFilterParams]);

  // الدخول بقسم/تصنيف يفتح من أول الصفحة دائماً (شكوى يونيو 2026)
  useEffect(() => { window.scrollTo(0, 0); }, [deptSlug, category]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const page = await fetchPage(0);
        setProducts(page);
        offsetRef.current = page.length;
        setHasMore(page.length >= PAGE_SIZE);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchPage(offsetRef.current);
      setProducts((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...page.filter((x) => !seen.has(x.id))];
      });
      offsetRef.current += page.length;
      setHasMore(page.length >= PAGE_SIZE);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  }, [fetchPage, hasMore, loadingMore]);

  // تحميل تلقائي عند الاقتراب من نهاية القائمة
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) loadMore(); }, { rootMargin: "700px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore, hasMore, loading]);

  // Full-catalog search: query the backend text index (surfaces every product
  // incl. community designs, not just the 60 loaded for the current view).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const params = { q: term, limit: 60, ...buildFilterParams() };
        if (deptSlug) params.department = deptSlug;
        const { data } = await http.get("/products", { params });
        setResults(data || []);
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, deptSlug, buildFilterParams]);

  // Departments-only navigation (customer request Aug 2026): category chips removed
  const currentDept = deptSlug ? departments.find((d) => d.slug === deptSlug) : null;
  const deptCounts = useDeptCounts();

  const filtered = useMemo(() => {
    if (results !== null) return results;   // backend search active
    return products;
  }, [products, results]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-5">
      <Seo title={lang === "ar" ? "المتجر — ورد وهدايا" : "Shop — Flowers & Gifts"} />
      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute top-1/2 -translate-y-1/2 opacity-60"
                style={{ [lang === "ar" ? "right" : "left"]: 14 }} />
        <input
          className="input-field"
          style={{ [lang === "ar" ? "paddingRight" : "paddingLeft"]: "2.5rem",
                   [lang === "ar" ? "paddingLeft" : "paddingRight"]: "3rem" }}
          placeholder={t("search")}
          value={q} onChange={(e) => { setQ(e.target.value); if (imgInfo) clearImageSearch(); }}
          data-testid="search-input"
        />
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
               className="hidden" onChange={onImageSelect} data-testid="image-search-file" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={imgSearching}
                title={lang === "ar" ? "بحث بالصورة" : "Search by image"}
                className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full transition-colors disabled:opacity-60"
                style={{ [lang === "ar" ? "left" : "right"]: 8, background: "rgba(0,0,0,0.18)", color: "var(--primary)" }}
                data-testid="image-search-btn">
          {imgSearching ? <Loader2 size={17} className="animate-spin" /> : <Camera size={17} />}
        </button>
      </div>

      {/* Image-search result banner */}
      {imgInfo && (
        <div className="rounded-2xl p-3.5 flex items-start gap-3" data-testid="image-search-banner"
             style={{ background: "linear-gradient(135deg,rgba(0,0,0,0.06),rgba(0,0,0,0.14))", border: "1px solid rgba(0,0,0,0.35)" }}>
          <Sparkles size={18} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: "var(--primary)" }}>
              {imgInfo.error
                ? (lang === "ar" ? "تعذّر تحليل الصورة، حاول بصورة أوضح" : "Couldn't analyse the image — try a clearer photo")
                : imgInfo.count > 0
                  ? (imgInfo.matchQuality === "exact_match"
                      ? (lang === "ar" ? `✅ وجدنا المنتج نفسه في الصورة${imgInfo.count > imgInfo.exactCount ? " + منتجات مشابهة" : ""}` : `✅ Found the exact product${imgInfo.count > imgInfo.exactCount ? " + similar items" : ""}`)
                      : imgInfo.matchQuality === "visual"
                        ? (lang === "ar" ? `🎯 وجدنا ${imgInfo.visualCount} مطابقة بصرية عالية + مشابهات` : `🎯 ${imgInfo.visualCount} strong visual matches + similar`)
                        : imgInfo.matchQuality === "similar"
                          ? (lang === "ar" ? `ما لقينا المنتج نفسه — هذه أقرب ${imgInfo.count} منتجات مشابهة 🌹` : `No exact match — here are the ${imgInfo.count} closest similar products 🌹`)
                          : (lang === "ar" ? `لقينا لك ${imgInfo.count} منتجاً مشابهاً 🌸` : `Found ${imgInfo.count} similar products 🌸`))
                  : (lang === "ar" ? "ما لقينا منتجاً مطابقاً، جرّب صورة ثانية" : "No close match — try another photo")}
            </div>
            {imgInfo.description && <div className="text-xs opacity-70 mt-0.5 line-clamp-2">{imgInfo.description}</div>}
            {imgInfo.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {imgInfo.keywords.slice(0, 6).map((k, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.6)", color: "var(--primary)" }}>{k}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={clearImageSearch} className="shrink-0 opacity-60 hover:opacity-100" data-testid="image-search-clear">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Browse-as-cards CTA — links to /browse for the photo-led experience */}
      <Link to="/browse"
            className="flex items-center justify-center gap-2 rounded-2xl py-2.5
                       text-[13px] font-semibold transition hover:opacity-90"
            style={{ background: "var(--primary)",
                     color: "#fff" }}
            data-testid="browse-as-cards-cta">
        <LayoutGrid size={15} />
        {lang === "ar" ? "تصفّح بالأقسام والصور" : "Browse by photo"}
      </Link>

      {/* Departments row (iter #48) */}
      {departments.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs opacity-60 flex items-center gap-1">
            <Layers size={11} /> {lang === "ar" ? "الأقسام" : "Departments"}
          </div>
          <div className="flex gap-2 overflow-x-auto scroll-x-hide pb-1"
                data-testid="shop-departments">
            <button
              className={`chip ${!deptSlug ? "active" : ""}`}
              onClick={() => setSp({})}
              data-testid="dept-all-btn">
              {lang === "ar" ? "الكل" : "All"}
            </button>
            {departments.map((d) => (
              <button key={d.id}
                       className={`chip ${deptSlug === d.slug ? "active" : ""}`}
                       onClick={() => setSp({ department: d.slug })}
                       data-testid={`dept-chip-${d.slug}`}
                       style={deptSlug === d.slug ? { background: d.color, color: "#fff", borderColor: d.color } : {}}>
                <span className="text-base leading-none">{d.icon}</span>
                <span>{lang === "ar" ? d.name_ar : d.name_en}</span>
                {deptCounts[d.id] > 0 && (
                  <span className="text-[10px] opacity-75" data-testid={`dept-count-${d.slug}`}>
                    ({deptCounts[d.id]})
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* تصنيفات القسم المحدد — دوائر بصورها وأسمائها، تظهر فقط داخل قسمها */}
          {(() => {
            const selDept = departments.find((d) => d.slug === deptSlug);
            const deptCats = selDept
              ? allCategories.filter((c) => c.department_id === selDept.id && !c.parent_id)
              : [];
            if (!deptCats.length) return null;
            return (
              <div className="flex gap-3 overflow-x-auto scroll-x-hide pb-1 pt-1.5"
                   data-testid="shop-dept-categories">
                <button onClick={() => setSp({ department: deptSlug })}
                        className="shrink-0 flex flex-col items-center gap-1 active:scale-95 transition-transform"
                        style={{ width: 66 }} data-testid="dept-cat-all-btn">
                  <span className="w-14 h-14 rounded-full flex items-center justify-center text-xl bg-white"
                        style={{ border: !category ? "2.5px solid #111" : "1.5px solid rgba(0,0,0,0.14)" }}>
                    🗂
                  </span>
                  <span className={`text-[10.5px] leading-tight text-center ${!category ? "font-black" : "font-semibold opacity-70"}`}>
                    {lang === "ar" ? "كل القسم" : "All"}
                  </span>
                </button>
                {deptCats.map((c) => (
                  <button key={c.id}
                          onClick={() => setSp({ department: deptSlug, category: c.slug, category_id: c.id })}
                          className="shrink-0 flex flex-col items-center gap-1 active:scale-95 transition-transform"
                          style={{ width: 66 }}
                          data-testid={`dept-cat-chip-${c.slug}`}>
                    <span className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center bg-white"
                          style={{ border: category === c.slug ? "2.5px solid #111" : "1.5px solid rgba(0,0,0,0.12)" }}>
                      {c.image ? (
                        <img src={imgOf(c.image)} alt="" loading="lazy"
                             className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl">{c.icon || "🎁"}</span>
                      )}
                    </span>
                    <span className={`text-[10.5px] leading-tight text-center w-full ${category === c.slug ? "font-black" : "font-semibold opacity-70"}`}
                          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {lang === "ar" ? c.name_ar : c.name_en || c.name_ar}
                    </span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Quick filters (budget / colour / occasion) + sort */}
      <div className="space-y-2" data-testid="quick-filters">
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters((v) => !v)}
                  className={`chip ${activeFilterCount > 0 ? "active" : ""}`}
                  data-testid="filters-toggle">
            <SlidersHorizontal size={14} />
            {lang === "ar" ? "الفلاتر" : "Filters"}
            {activeFilterCount > 0 && (
              <span className="ms-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                    style={{ background: "var(--secondary)", color: "#3a2c10" }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <div className="flex gap-2 overflow-x-auto scroll-x-hide pb-1 flex-1">
            {SORTS.map((s) => (
              <button key={s.key}
                      onClick={() => setSortKey(s.key)}
                      className={`chip ${sortKey === s.key ? "active" : ""}`}
                      data-testid={`sort-${s.key}`}>
                {lang === "ar" ? s.ar : s.en}
              </button>
            ))}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
                    className="text-xs opacity-60 hover:opacity-100 shrink-0"
                    data-testid="filters-clear">
              {lang === "ar" ? "مسح الكل" : "Clear"}
            </button>
          )}
        </div>

        <AnimatePresence initial={false}>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="space-y-3 overflow-hidden card-soft p-3">
              <div>
                <div className="text-xs opacity-60 mb-1.5">{lang === "ar" ? "الميزانية (ر.ق)" : "Budget (QAR)"}</div>
                <div className="flex gap-2 flex-wrap">
                  {BUDGETS.map((b, i) => (
                    <button key={i}
                            onClick={() => setBudgetIdx(budgetIdx === i ? null : i)}
                            className={`chip ${budgetIdx === i ? "active" : ""}`}
                            data-testid={`budget-${i}`}>
                      {lang === "ar" ? b.ar : b.en}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs opacity-60 mb-1.5">{lang === "ar" ? "اللون" : "Color"}</div>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button key={c.val}
                            onClick={() => setColor(color === c.val ? "" : c.val)}
                            className={`chip ${color === c.val ? "active" : ""}`}
                            data-testid={`color-${c.en.toLowerCase()}`}>
                      <span className="w-3 h-3 rounded-full border border-black/20" style={{ background: c.dot }} />
                      {lang === "ar" ? c.ar : c.en}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs opacity-60 mb-1.5">{lang === "ar" ? "المناسبة" : "Occasion"}</div>
                <div className="flex gap-2 flex-wrap">
                  {OCCASIONS.map((o) => (
                    <button key={o.val}
                            onClick={() => setOccasion(occasion === o.val ? "" : o.val)}
                            className={`chip ${occasion === o.val ? "active" : ""}`}
                            data-testid={`occasion-${o.val}`}>
                      {lang === "ar" ? o.ar : o.en}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Departments-first navigation — categories now appear as a sub-row ONLY
           after picking a department (user request Jun 2026). Old /shop?category=
           deep links still work. */}

      {(loading || searching || imgSearching) ? (
        <ProductGridSkeleton count={8} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="products-grid">
          {filtered.map((p) => (
            p.match_type === "exact" || p.match_type === "visual" ? (
              <div key={p.id} className="relative">
                <span className="absolute top-2 start-2 z-10 text-[10px] font-bold px-2 py-0.5 rounded-full shadow"
                      style={p.match_type === "exact"
                        ? { background: "#059669", color: "#fff" }
                        : { background: "#2563eb", color: "#fff" }}
                      data-testid={`match-badge-${p.id}`}>
                  {p.match_type === "exact"
                    ? (lang === "ar" ? "✓ المنتج نفسه" : "✓ Exact match")
                    : (lang === "ar" ? "مطابقة بصرية" : "Visual match")}
                </span>
                <ProductCard product={p} />
              </div>
            ) : (
              <ProductCard key={p.id} product={p} />
            )
          ))}
        </div>
      )}
      {!loading && !searching && !imgSearching && filtered.length === 0 && (
        <div className="text-center py-16 opacity-60">{t("noItems")}</div>
      )}

      {/* عرض المزيد — يظهر كل المنتجات دفعة بعد دفعة (تلقائياً عند التمرير أو بالزر) */}
      {results === null && !loading && hasMore && (
        <div ref={sentinelRef} className="flex justify-center pt-1 pb-4">
          <button onClick={loadMore} disabled={loadingMore}
                  className="chip !px-5 !py-2.5 text-[13px] font-bold disabled:opacity-60"
                  data-testid="load-more-btn">
            {loadingMore
              ? <><Loader2 size={14} className="animate-spin" /> {lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</>
              : (lang === "ar" ? `عرض المزيد — ${products.length} منتج معروض` : `Load more — ${products.length} shown`)}
          </button>
        </div>
      )}
    </div>
  );
}
